import { formatCurrency } from "@/utils/currency";

export function pct(n: number) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, "");
}

/** "$5.00 per order" or "12%". */
export function rateText(value: number, type: "PERCENTAGE" | "FIXED") {
  return type === "FIXED" ? `${formatCurrency(value)} per order` : `${pct(value)}%`;
}

/** Whole dollars when there are no cents: $40 rather than $40.00. */
export function money(value: number) {
  return formatCurrency(value).replace(/\.00$/, "");
}

export function relativeTime(iso: string) {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return shortDate(iso);
}

export function shortDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function monthYear(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/**
 * How a figure moved against the prior period, kept short. A first order
 * never shows up as "+100%" against nothing.
 */
export function describeChange(
  current: number,
  previous: number,
  periodDays: number,
  format: (v: number) => string
): { text: string; direction: "up" | "down" | "flat" } {
  const prev = Number(previous) || 0;
  const cur = Number(current) || 0;
  if (prev === 0) {
    return { text: `Prior ${periodDays} days: ${format(0)}`, direction: cur > 0 ? "up" : "flat" };
  }
  if (cur === prev) return { text: `Same as prior ${periodDays} days`, direction: "flat" };
  const change = Math.round(((cur - prev) / prev) * 100);
  return {
    text: `${change > 0 ? "+" : ""}${change}% vs prior ${periodDays} days`,
    direction: change > 0 ? "up" : "down",
  };
}
